"use client";

import React from "react";
import { FontAwesomeIcon, faCheck, faCopy, faTag } from "@/constants/icons";
import { Offer } from "@/services/dataTypes";
import OfferOutUrl from "@/components/shared/OfferOutUrl";
import OfferDetailsToggle from "./OfferDetailsToggle";

/* ------------------------------------------------------------------ */
function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/* ------------------------------------------------------------------ */

export interface SavingsOffer {
  discountTag: string | null;
  isCoupon: boolean;
  couponCode: string | null;
  offerTitle: string;
  offerSubtitle: string;
  merchantName: string;
  merchantLogo: string;
  merchantHref: string;
  uniqueId: string;
  outUrl: string;
  domain: string;
  productImage: string;
  merchantImg: string;
  rawOffer: Offer;
  updatedAt: string | null;
}

interface Props {
  offer: SavingsOffer;
}

/* ------------------------------------------------------------------ */

const SavingsOfferCard = ({ offer }: Props) => {
  const {
    discountTag,
    isCoupon,
    couponCode,
    offerTitle,
    offerSubtitle,
    merchantName,
    merchantHref,
    uniqueId,
    outUrl,
    domain,
    productImage,
    merchantImg,
    rawOffer,
    updatedAt,
  } = offer;

  const ago = timeAgo(updatedAt);

  return (
    <article className="group relative flex flex-col bg-white rounded-3xl overflow-hidden border border-[#E5E7EB] shadow-[0_4px_20px_-8px_rgba(15,23,42,0.12)] hover:shadow-[0_12px_32px_-8px_rgba(15,23,42,0.18)] hover:-translate-y-1 transition-all duration-300 h-full min-h-[300px]">

      {/* ═══════════ CARD BODY ═══════════ */}
      <div className="flex flex-col flex-1 p-5 gap-4">

        {/* Row 1: merchant name | badge */}
        <div className="flex items-start justify-between gap-3">
          <span className="text-[13px] font-bold text-[#1E3A5F] leading-none">
            {merchantName}
          </span>

          {isCoupon ? (
            <span className="shrink-0 inline-flex items-center gap-1 bg-[#DCFCE7] text-[#15803D] text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full">
              <FontAwesomeIcon icon={faTag} className="w-2 h-2" />
              Coupon
            </span>
          ) : discountTag ? (
            <span className="shrink-0 inline-flex items-center bg-[#DC2626] text-white text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full">
              {discountTag}
            </span>
          ) : (
            <span className="shrink-0 inline-flex items-center gap-1 bg-[#DCFCE7] text-[#15803D] text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full">
              <FontAwesomeIcon icon={faCheck} className="w-2 h-2" />
              Verified
            </span>
          )}
        </div>

        {/* Offer title */}
        <h3 className="text-[20px] font-extrabold text-[#111827] leading-snug line-clamp-3 m-0">
          {offerTitle}
        </h3>

        {/* Subtitle */}
        <p className="text-[13px] text-[#4B5563] leading-snug m-0 line-clamp-1">
          {offerSubtitle}
        </p>

        <div className="flex-1" />

        {/* CTA button */}
        {isCoupon ? (
          <OfferOutUrl
            unique_id={uniqueId}
            outUrl={outUrl}
            merchantHref={merchantHref}
            domain={domain}
            customClass="relative w-full py-3 rounded-2xl bg-[#F97316] hover:bg-[#EA580C] flex items-center justify-center overflow-hidden group/btn no-underline transition-all duration-200"
          >
            <span className="text-[13px] font-black text-white uppercase tracking-widest transition-transform duration-300 group-hover/btn:-translate-y-8 flex items-center gap-1.5">
              <FontAwesomeIcon icon={faTag} className="w-3 h-3" />
              Show Code
            </span>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 translate-y-8 group-hover/btn:translate-y-0 transition-transform duration-300 bg-[#EA580C] rounded-2xl">
              <FontAwesomeIcon icon={faCopy} className="w-3 h-3 text-white" />
              <span className="text-[11px] font-black text-white uppercase tracking-widest">
                {couponCode?.trim().split(" ")[0].slice(0, 10)}
              </span>
            </div>
          </OfferOutUrl>
        ) : (
          <OfferOutUrl
            unique_id={uniqueId}
            outUrl={outUrl}
            merchantHref={merchantHref}
            domain={domain}
            customClass="w-full py-3 rounded-2xl bg-[#F97316] text-white text-[13px] font-black flex items-center justify-center no-underline hover:bg-[#EA580C] transition-all duration-200 shadow-sm"
          >
            Get Deal
          </OfferOutUrl>
        )}
      </div>

      {/* ═══════════ FOOTER ROW ═══════════ */}
      <div className="flex items-center gap-3 px-5 py-3 border-t border-[#E5E7EB] bg-[#F8FAFC]">

        {/* Verified */}
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-full border border-[#15803D] flex items-center justify-center">
            <FontAwesomeIcon icon={faCheck} className="w-2 h-2 text-[#15803D]" />
          </div>
          <span className="text-[11px] text-[#4B5563] font-medium">Verified</span>
        </div>

        <span className="text-[#E5E7EB] text-xs">•</span>

        {/* Time ago */}
        {ago && (
          <div className="flex items-center gap-1.5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-3.5 h-3.5 text-[#4B5563]"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-[11px] text-[#4B5563] font-medium">{ago}</span>
          </div>
        )}

        {/* View Details - far right */}
        <div className="ml-auto">
          <OfferDetailsToggle
            domain={domain}
            imageSrc={productImage}
            merchantHref={merchantHref}
            offer={rawOffer}
            type="anchor"
            merchantImg={merchantImg}
          />
        </div>
      </div>

    </article>
  );
};

export default SavingsOfferCard;
